// GEO_EXEC_R14 · Distribution — the channel drafts Maya writes and the client sends.
//
// Nothing here posts anything: LinkedIn's and Reddit's terms both forbid automated posting, so the
// product writes, the client edits, the client sends, and `markDraftSent` records what they did.
//
// Everything below the request wrappers is pure. The view holds no rules of its own — status
// labels, the quota meters, the headline of a row and the one sentence shown for a refusal all
// live here, where they can be tested without a browser.

import { api } from "./client.js";

const BASE = "/api/geo-exec/r14";

export function listDistribution(brandId, { signal } = {}) {
  return api(`${BASE}/brands/${brandId}/distribution`, { signal });
}

export function createDraft(payload, { idempotencyKey = "", signal } = {}) {
  return api(`${BASE}/distribution`, {
    method: "POST",
    json: payload,
    signal,
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
  });
}

export function saveDraftEdit(itemId, brandId, bodyEdited) {
  return api(`${BASE}/distribution/${itemId}`, {
    method: "PATCH",
    json: { brand_id: brandId, body_edited: bodyEdited },
  });
}

export function markDraftSent(itemId, brandId, url) {
  return api(`${BASE}/distribution/${itemId}/sent`, {
    method: "POST",
    json: { brand_id: brandId, url },
  });
}

export function regenerateDraft(itemId, brandId, angle = "") {
  return api(`${BASE}/distribution/${itemId}/regenerate`, {
    method: "POST",
    json: { brand_id: brandId, angle },
  });
}

// ── the two channels ────────────────────────────────────────────────────────────────────────────

export const DISTRIBUTION_CHANNELS = [
  { id: "linkedin", label: "LinkedIn" },
  { id: "reddit", label: "Reddit" },
];

export function channelLabel(channel) {
  const found = DISTRIBUTION_CHANNELS.find((entry) => entry.id === String(channel || ""));
  return found ? found.label : String(channel || "");
}

// ── the state machine, as the client reads it ───────────────────────────────────────────────────
//
// `drafted → edited → sent → cited`. Only `cited` is written by us rather than by the client, which
// is why it is the only one that reads as an achievement rather than as a step.

export const DISTRIBUTION_STATUSES = ["drafted", "edited", "sent", "cited"];

const STATUS_META = {
  drafted: { label: "Draft", tone: "draft", hint: "Written, not sent yet." },
  edited: { label: "Edited", tone: "draft", hint: "You rewrote it. Your version is what gets copied." },
  sent: { label: "Sent", tone: "ok", hint: "Posted. Waiting to see whether AI starts quoting it." },
  cited: { label: "Cited", tone: "cited", hint: "An AI answer quoted this post." },
  cancelled: { label: "Cancelled", tone: "muted", hint: "" },
};

export function statusMeta(status) {
  return STATUS_META[String(status || "")] || { label: String(status || ""), tone: "muted", hint: "" };
}

/** The line the list shows.
 *
 * There is no title column on an R14 row and there should not be one: a LinkedIn post opens with
 * the buyer's query verbatim and a Reddit answer opens by answering the person, so the first line
 * of the piece IS its headline. Falling back to the stored question keeps a row readable even when
 * the body is empty, which is the only state that would otherwise render a blank row.
 */
export function draftHeadline(item, max = 120) {
  const body = String(item?.current_body || item?.body_edited || item?.body || "");
  const first = body.split("\n").map((line) => line.trim()).find(Boolean) || "";
  const text = first || draftQuestion(item) || `${channelLabel(item?.channel)} draft`;
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/** The buyer question this piece answers, stamped onto `target` when the draft was written. */
export function draftQuestion(item) {
  return String(item?.target?.question || "").trim();
}

/** The second line of a row — the question, unless the piece already opens with it.
 *
 * A LinkedIn post is REQUIRED to open with the buyer's query verbatim, so on that channel the
 * headline and the question are usually the same string. Printing both is the page saying the
 * same sentence twice. */
export function draftSubline(item, max = 120) {
  const question = draftQuestion(item);
  if (!question) return "";
  const headline = draftHeadline(item, max);
  const trimmed = headline.replace(/…$/, "").trim();
  return question === headline || question.startsWith(trimmed) ? "" : question;
}

export function draftAngle(item) {
  const angle = String(item?.target?.angle || "").trim();
  return angle ? angle.replace(/_/g, " ") : "";
}

/** Reddit's rule is "answer a thread that exists". Without a target URL the draft is honest about
 *  being generic, and the client has to find the thread themselves — so the page says so. */
export function needsThread(item) {
  return String(item?.channel || "") === "reddit" && String(item?.target?.kind || "") !== "thread";
}

export function formatStamp(seconds, locale) {
  if (!seconds) return "";
  const date = new Date(Number(seconds) * 1000);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString(locale || undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function citedLabel(item, locale) {
  const stamp = formatStamp(item?.cited_at, locale);
  return stamp ? `Cited by AI on ${stamp}` : "Cited by AI";
}

// ── the month's quota ───────────────────────────────────────────────────────────────────────────

const QUOTA_LABELS = { total: "drafts this month", reddit: "of them on Reddit" };

/** `{total: {used, cap}, reddit: {...}}` → the meters above the list, total always first.
 *
 * An absent quota (an older build of the API) renders nothing rather than a zero: "0 of 0 used" is
 * a cap the client does not have, and inventing one here would be the front end lying about a
 * limit the server never set. */
export function quotaRows(quota) {
  if (!quota || typeof quota !== "object") return [];
  const order = ["total", ...Object.keys(quota).filter((key) => key !== "total")];
  return order
    .filter((key) => quota[key] && Number(quota[key].cap) > 0)
    .map((key) => {
      const used = Math.max(0, Number(quota[key].used) || 0);
      const cap = Math.max(0, Number(quota[key].cap) || 0);
      return {
        id: key,
        label: QUOTA_LABELS[key] || key,
        used,
        cap,
        left: Math.max(0, cap - used),
        full: used >= cap,
        pct: cap ? Math.min(100, Math.round((used / cap) * 100)) : 0,
      };
    });
}

// ── refusals ────────────────────────────────────────────────────────────────────────────────────
//
// The API writes a client-facing sentence for every refusal it has one for; these are the ones it
// answers with a bare code. One sentence each, and each one says what to do next.

const SENT_URL_REASONS = {
  missing: "Paste the link to the post first.",
  wrong_channel: "That link is not on this channel, so it cannot be the post. Paste the link to the post itself.",
  short_link:
    "That is a shortened link. Paste the full post address — a short link is not what AI will quote, so a citation could never be matched back to it.",
  not_a_post:
    "That is the right site but not a post — it looks like a profile or a feed. Open the post itself and copy its address.",
  unreachable: "I could not open that link, so I have not marked it as sent. Check it opens in a private window.",
};

const ERROR_FALLBACKS = {
  autopilot_required: "Writing channel drafts is an Autopilot-plan feature.",
  monthly_cap_reached: "You have used this month's drafts. The cap resets at the start of next month.",
  too_similar:
    "This came out as the same piece your other channel already has, so I have not stored it. Try another angle or another source.",
  source_not_ready: "That page is not live yet, so there is nothing for the post to point at. Publish it first.",
  invalid_target_url: "That target link is not a thread on this channel.",
  invalid_sent_url: "That link is not the address of the post.",
  draft_failed: "The draft did not come back. Try again in a moment.",
  body_required: "There is nothing to save — the draft is empty.",
  unknown_channel: "That channel is not one of the two this writes for.",
  unknown_source_type: "That is not something a channel piece can be written from.",
  not_found: "That draft is gone.",
};

/** One English sentence for a failed call, preferring the server's own wording.
 *
 * The server writes the sentence for every refusal the client can act on (the caps, the similarity
 * gate, the sent-link checks), and those sentences carry numbers this code does not have. We only
 * supply words when it answered with a bare code. */
export function distributionErrorMessage(error) {
  const payload = error?.payload || {};
  const server = String(payload.message || "").trim();
  if (server) return server;
  const code = String(payload.error || "").trim();
  if (code === "invalid_sent_url") {
    return SENT_URL_REASONS[String(payload.reason || "")] || ERROR_FALLBACKS.invalid_sent_url;
  }
  if (ERROR_FALLBACKS[code]) return ERROR_FALLBACKS[code];
  if (error?.status === 402) return ERROR_FALLBACKS.autopilot_required;
  return String(error?.message || "Something went wrong. Try again.");
}

/** Was this the switch being off, rather than something the client did? */
export function isFeatureOff(error) {
  return error?.status === 404 && String(error?.payload?.error || "") === "not found";
}

// ── what a new draft can be written from ────────────────────────────────────────────────────────

/** R1 clusters and R2 comparison pages, plus the client's own facts, as one list of choices.
 *
 * Only PUBLISHED pages are offered. The API answers `source_not_ready` for the rest, and a picker
 * that lets the client choose an option that is guaranteed to be refused is a picker that teaches
 * them the product is broken. Brand facts are always last and always available: they are the one
 * source that needs no page. */
export function draftSourceOptions({ clusters = [], pages = [] } = {}) {
  const out = [];
  for (const cluster of clusters) {
    if (String(cluster?.page?.state || "") !== "published") continue;
    const question = primaryQuestion(cluster);
    out.push({
      key: `cluster:${cluster.cluster_id}`,
      source_type: "cluster",
      source_id: cluster.cluster_id,
      label: question || String(cluster.label || `Cluster ${cluster.cluster_id}`),
      note: question ? String(cluster.label || "") : "Answer page",
    });
  }
  for (const page of pages) {
    if (String(page?.state || "") !== "published") continue;
    const rivals = (page.competitors || []).map((entry) => entry.name).filter(Boolean).join(", ");
    out.push({
      key: `comparison_page:${page.page_id}`,
      source_type: "comparison_page",
      source_id: page.page_id,
      label: String(page.page_key || `Comparison page ${page.page_id}`).replace(/[-_]+/g, " "),
      note: rivals ? `Comparison · ${rivals}` : "Comparison page",
    });
  }
  out.push({
    key: "brand_fact:",
    source_type: "brand_fact",
    source_id: null,
    label: "What you can prove about your brand",
    note: "Your stated facts and materials — no page needed",
  });
  return out;
}

export function primaryQuestion(cluster) {
  const questions = cluster?.questions || [];
  const primary = questions.find((entry) => entry.role === "primary") || questions[0] || {};
  return String(primary.question || "").trim();
}

/** R1 and R2 have their own switches. Either being off is not an error the client should read
 *  about — it just means that kind of source is not on offer today. */
export async function loadDraftSources(brandId, { signal } = {}) {
  const [clusters, pages] = await Promise.all([
    api(`/api/geo-exec/r1/brands/${brandId}/clusters`, { signal }).catch(() => null),
    api(`/api/geo-exec/r2/brands/${brandId}/comparison-pages`, { signal }).catch(() => null),
  ]);
  return draftSourceOptions({ clusters: clusters?.clusters || [], pages: pages?.pages || [] });
}

export function newIdempotencyKey() {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {
    // A crypto-less environment still gets a key; it only has to be unique per click.
  }
  return `r14-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
